import { ChatType } from "@/server/types/chat";
import { MemberRole } from "@/server/types/member";
import { MessageCategory } from "@/server/types/message";

export enum MessageSendingStatus {
  SENT = 1, // successfully sent and acknowledged by the server
  PENDING = 2, // in the process of being sent [OR DELETING - to be discussed], awaiting server acknowledgment
  FAILED = 3, // failed to send (e.g., network error), not acknowledged by the server
  DELIVERED = 4, // based on socket event, message received by the server (for messages sent from other devices)
  READ = 5, // to be implemented based on read receipts, message read by recipient(s) (JUST ON P2P??)
  // on group chats, we can have a "delivered" status based on socket events when message is delivered to all members (except sender)
  // or just add the read status based on socket events and chat type and not track delivered status for group chats to avoid complexity
}

export enum InitChatStatus {
  SUCCESS = 1,
  PENDING = 2,
  FAILED = 3,
}

export enum ReactionSendingStatus {
  SUCCESS = 1,
  PENDING = 2,
  FAILED = 3,
}

export type ChatAvatarType = {
  attachmentId?: string;
  content?: Blob;
  secondMemberId?: string; // used for p2p chats - pull avatar from members
};

export type ChatEntity = {
  id: string;
  hubId: string | null; // for ai chats
  eCSK: string;
  currentEpoch: number; // CSK rotation epoch (0 = original key)
  name: string;
  description?: string;
  type: ChatType;
  avatar?: ChatAvatarType;
  timestamp: number;
  memberCount: number;
  unreadCount: number;
  isMuted: boolean;
  isPinned: boolean;
  isArchived: boolean;
  isPublic: boolean; // for searchable group chats
  allowThreads: boolean; // for group chats only
  canPost: MemberRole; // for group chats only
  pinnedMessageIds?: string[];

  roleAsMember: {
    username: string;
    role: MemberRole;
    removed?: boolean;
  };
  lastMessage: MessageEntity | null;

  /** Sync-owned: encrypted last message from BE for post-sync hydration. */
  lastMessageCiphertext?: {
    id: string;
    chatId: string;
    sender: string;
    // Read-time anonymisation marker on the chat-list lastMessage DTO. NOTE
    // the wire casing split: the chat-list DTO carries camelCase
    // `senderDeleted`, message payloads carry snake_case `sender_deleted` —
    // normalizeLastMessageCiphertext reads both. Present only when true.
    senderDeleted?: boolean;
    content: string;
    attachmentMeta: string | null;
    category: number;
    isEdited: boolean;
    isOutgoing: boolean;
    clientId: string | null;
    parentId: string | null;
    threadId: string | null;
    commentCount: number | null;
    senderAvatar: string | null;
  } | null;

  // TO DO => move to separate sync collection
  // Chat initialization status
  initStatus: InitChatStatus;
  lastMessageTimestamp?: string;
  firstMessageId?: string;
  lastSyncedAt: string;
  firstPhaseMsgListInit: boolean;
  fullMsgListInit: boolean;

  // Temporary
  batchCount?: number; // batches of messages loaded via progresive loading
  renderedBatchCount?: number; // batches of messages rendered in the UI via infinite scroll
};

export type UserReaction = {
  content: string;
  messageId: string;
  sendingStatus: ReactionSendingStatus;
};

// TO DO => Consider a flat structure
// COnsider storing Thread data and message UI state data to differect collection
// check performance
export type MessageEntity = {
  _clientId: string; // client id for optimistic updates and local db management, not sent by server
  id: string; // this now bcomes the server id, generated by server and sent back in response, used for all message operations and local db management
  chatId: string;
  sender: string;
  // True when the server replaced `sender` with the deleted-user label at read
  // time (account deletion, BE contract §5). Present only when true — mirrors
  // the wire semantics ("absent, not false"). Branch on this, never on the
  // label string. NOT set on service messages (their sender is exempt).
  senderDeleted?: boolean;
  avatarId?: string | null;
  timestamp: string;
  category: MessageCategory;
  content: MessageContentEntity;
  hasAttachment: boolean;
  isEdited: boolean;
  isOutgoing: boolean;
  isPinned?: boolean;
  // reply logic
  isReply: boolean;
  parentMessage?: {
    id: string;
    timestamp?: string;
    category: MessageCategory;
  };
  tags?: string[];

  // comment logic
  commentCount?: number | null; // total amount of comments
  senderAvatar: string | null; // sender's avatar
  parentCommentCount: number | null;
  // comment thread
  threadId: string | null;
  commentSentAt: string | null;
  // comment pagination metadata
  commentFullMsgListInit?: boolean; // all comments loaded
  commentFirstPhaseMsgListInit?: boolean; // first phase of loading complete
  commentLastSyncedAt?: string; // last sync timestamp
  commentFirstCommentTimestamp?: string; // first comment timestamp for pagination
  commentFirstCommentId?: string; // first comment id for pagination cursor
  commentBatchCount?: number; // number of batches loaded
  commentRenderedBatchCount?: number; // number of batches rendered
  commentTake?: number; // number of comments to take in next batch

  sendingStatus: MessageSendingStatus;
  /**
   * Set by the boot stranded-send reconciliation (`confirmSendByClientId`, #1177)
   * when a stranded PENDING send could NOT be confirmed against the server —
   * offline at boot, the lookup window wasn't exhausted, or the lookup errored.
   * The row is parked on FAILED so the spinner stops, but this flag marks it as
   * "delivery not confirmed" rather than a real failure: the UI renders a neutral
   * "Not confirmed sent" instead of the red failed tick, and delete is suppressed
   * (a possibly-delivered message must never be locally dropped). Distinct from a
   * plain FAILED, which is a server-confirmed absence and stays deletable.
   *
   * ONLY meaningful when `sendingStatus === FAILED` — every reader gates on FAILED
   * first. A later heal (socket ack / sync) may move the row to DELIVERED/READ and
   * leave this flag set; that is inert (nothing downgrades a healed row to FAILED),
   * so heal paths are not required to clear it.
   */
  deliveryUnconfirmed?: boolean;
  reactions?: Record<string, number>;
  userReaction?: UserReaction;

  /**
   * Decryption status for this message. Set by the websocket handler and
   * the retry orchestrator.
   *
   * - "ok"     – decrypted successfully
   * - "failed" – decryption threw; `content.text` is persisted as `""` and
   *              consumers should render based on this flag, not the empty
   *              text. The retry orchestrator will replace the content once
   *              a retry succeeds.
   * - undefined – legacy row, predates this field
   */
  decryptionStatus?: "ok" | "failed";
  /**
   * Total decryption attempts made on this row, regardless of which
   * component performed them. The websocket handler and chat-sync paths set
   * this to `1` on the initial failure; the retry orchestrator then
   * increments it on each subsequent attempt and caps at 5. Drives the UI
   * state machine ("Decrypting…" vs "Unable to decrypt").
   */
  decryptionAttempts?: number;

  /**
   * Sync-owned ciphertext-at-rest sidecar (Dexie v14 / prior-art gap "G2").
   *
   * When a row is persisted in a not-yet-decryptable state — the offline
   * backfill pager or the websocket handler stored it before the chat's
   * `eCSK` was available, or decryption threw — the original *encrypted*
   * payload is retained here so the retry orchestrator / lazy-decrypt
   * subsystem can re-attempt decryption from these LOCAL bytes instead of
   * re-fetching the message from the server (`GET /api/messages/:id`, the
   * per-message refetch storm). Mirrors the `ChatEntity.lastMessageCiphertext`
   * precedent and feeds the same inputs the proven live-socket path uses:
   * `decryptMessageContent(eCSK, content, attachmentMeta)` for regular
   * messages and `decryptServiceMessageContent` (which reads `content` +
   * the row's `sender`) for service messages.
   *
   * Lifecycle:
   * - Written ONLY for rows that still need decryption
   *   (`decryptionStatus: "failed"`).
   * - Cleared (set back to `undefined`) once the row decrypts successfully,
   *   to bound how long encrypted payloads linger at rest.
   * - Absent on successfully-decrypted rows AND on legacy `failed` rows that
   *   predate this field (their ciphertext was already discarded → the
   *   orchestrator keeps its `getMessage` fallback for those only).
   *
   * Not indexed and never rendered by the UI — purely a decryption input.
   */
  ciphertext?: {
    /** Raw encrypted message body (server `content`). */
    content: string;
    /** Raw encrypted attachment metadata (server `attachment_meta`), or null. */
    attachmentMeta: string | null;
  };

  loadingTop?: boolean;
  loadingBottom?: boolean;
  keyEpoch?: number | null;
};

export type MessageContentEntity = {
  /**
   * Decrypted message text. Set to "" for messages whose decryption is
   * pending/failed; consumers should always check `decryptionStatus` on the
   * parent `MessageEntity` before rendering the text.
   */
  text: string;
  link?: string; // process first link for now
  media?: AttachmentMeta; // img & video
  file?: AttachmentMeta; // pdfs for now
  voice?: VoiceAttachmentMeta; // voice msg
  /**
   * Set when the message text decrypted successfully but its attachment
   * meta did not. The text is still rendered; the attachment is shown in
   * a "broken" state.
   */
  attachmentDecryptionFailed?: boolean;
};

/**
 * Upload state embedded in AttachmentMeta for in-progress chunked uploads.
 * Populated by the sender so receivers can start streaming before GCS assembly.
 */
export type UploadState = {
  status: "uploading" | "complete" | "failed";
};

// TO DO => set a base type and extend afterward each type
export type AttachmentMeta = {
  attachmentId: string; // encrypted
  attachmentThumbnailId?: string; // encrypted
  filename: string;
  fileSize: number;
  fileType: string; // extension or mime type
  isLargeAttachment?: boolean; // ??
  /** Present while a chunked upload is in progress or has just completed/failed */
  uploadState?: UploadState;
  /**
   * Base64 ThumbHash — a ~25 byte blurred-preview placeholder for images and
   * videos, generated at send time and carried inside the (encrypted) meta so
   * a received bubble can paint an instant blur before any attachment fetch.
   * Absent on documents and on messages sent before this shipped.
   */
  placeholder?: string;
  /** Original media pixel dimensions, for aspect-correct bubbles. Absent on legacy messages. */
  width?: number;
  height?: number;
};

/** Where attachment content is stored */
export type AttachmentStorageLocation = "opfs" | "indexeddb";

export type VoiceAttachmentMeta = AttachmentMeta & {
  duration: number; // ms
  waveform: number[];
};

type BaseAttachmentEntity = {
  id: string; // attachment id
  chatId: string;
  messageId: string;
  timestamp: string; // TO DO => since we use uuid7 - check if needed - check sorting implications
  content?: Blob; // full file => to be processed
  fileType: string;
  filename: string;
  /**
   * LOCAL-ONLY recency marker (epoch ms): when this attachment's CONTENT was
   * last read or (re)written on this device. Never sent anywhere. Storage
   * eviction orders by it (falling back to the message timestamp for rows
   * that predate the field) — `timestamp` is the MESSAGE's send time, and
   * evicting by that threw away what the user watched a minute ago whenever
   * its message happened to be old.
   */
  lastAccessedAt?: number;
};

export type MediaAttachmentEntity = BaseAttachmentEntity & {
  thumbnail?: Blob;
  contentStoredIn?: AttachmentStorageLocation; // where full content is stored
  isLargeAttachment?: boolean;
  /** Base64 ThumbHash carried from the message meta, for an instant viewer blur. */
  placeholder?: string;
  /**
   * Native pixel dimensions carried from the message meta, so the viewer can
   * size its frame on the FIRST paint (no 100%→native shrink on open/swipe).
   */
  width?: number;
  height?: number;
  /**
   * Plaintext size + upload state carried from the message meta (in-memory
   * only, for the viewer nav list — never persisted to the table). A chunked
   * video navigated to via arrows/swipe needs both to register its SW stream,
   * the same way a direct open passes them through openDialog options.
   */
  fileSize?: number;
  uploadState?: UploadState;
};

export type FileAttachmentEntity = BaseAttachmentEntity & {
  contentStoredIn?: AttachmentStorageLocation; // where full content is stored
  fileType?: string;
  filename?: string;
  fileSize?: number;
};

export type VoiceAttachmentEntity = BaseAttachmentEntity & {
  // duration: number; // in seconds
  // waveform?: number[]; // for visual representation of the audio, amplitude bars
};

export type LinkEntity = {
  id: string; // message id => process one link per message for now
  chatId: string;
  timestamp: string; // TO DO => since we use uuid7 - check if needed - check sorting implications
  url: string;
  domainUrl?: string;
  faviconUrl?: string;
  metaTitle?: string;
  metaDescription?: string;
  ogImage?: string;
  withMetadata: boolean;
};

export type UserEntity = {
  id: string;
  avatarId?: string;
  avatar?: Blob;
};

/**
 * Stored epoch payload for CSK rotation warm cache.
 * Contains a self-encapsulated eCSK for a specific chat + epoch,
 * allowing the user to re-derive the CSK without hitting the server.
 */
export type StoredEpochPayload = {
  id: string; // compound key: `${chatId}:${epoch}`
  chatId: string;
  epoch: number;
  selfECSK: string; // self-encapsulated eCSK (hex)
  storedAt: number; // timestamp in ms
};
